import React, { forwardRef, useCallback, useEffect, useState } from "react";
import { isEqual } from "lodash";
import { KeyboardController } from "react-native-keyboard-controller";
import BaseField, { BaseFieldProps } from "./BaseField";
import { SelectChooser, SelectChooserProps } from "./select/SelectChooser";
import { processSelectItems } from "../utils";
import { PressableWithFocus } from "./PressableWithFocus";
import { theme } from "../theme";
import { ThemeProvider } from "../contexts/ThemeManager";
import { TextInput } from "react-native";

type SelectFieldProps<V> = Omit<BaseFieldProps, "component" | "hasValue" | "value"> &
    Omit<SelectChooserProps<V>, "onDismiss" | "visible" | "selectedValue" | "searchItems"> & {
        data: SelectChooserProps<V>["searchItems"];
        value?: SelectChooserProps<V>["selectedValue"];
        searchPlaceholder?: SelectChooserProps<V>["placeholder"];
        placeholder?: string;
        bottomComponent?: React.ReactNode;
        info?: React.ReactNode;
    };

// eslint-disable-next-line react/display-name
const SelectField = forwardRef(
    <V,>(
        {
            onSelect: propsOnSelect,
            data,
            value,
            asListAction,
            searchPlaceholder,
            isSelfContained,
            label,
            style,
            error,
            left,
            placeholder,
            right = !left ? (Array.isArray(data) ? "chevron-down" : "search") : undefined,
            onBlur,
            numberOfLines,
            testID,
            bottomComponent,
            info,
            labelLeftPadding,
            ...props
        }: SelectFieldProps<V>,
        ref: React.ForwardedRef<TextInput>,
    ) => {
        const [isVisible, setIsVisible] = useState(false);
        const onSelect = useCallback<NonNullable<SelectChooserProps<V>["onSelect"]>>(
            (item) => {
                void propsOnSelect?.(item);
                setIsVisible(false);
                onBlur?.();
            },
            [propsOnSelect, onBlur],
        );

        const getInitialValue = useCallback(() => {
            if (Array.isArray(data)) {
                return data.find((item) => isEqual(item.value, value) || isEqual(item, value))
                    ?.label;
            }
        }, [data, value]);

        const [valueLabel, setValueLabel] = useState(getInitialValue());

        useEffect(() => {
            processSelectItems<V>(data, undefined, (items) => {
                const ret = items.find(
                    (item) => isEqual(item.value, value) || isEqual(item, value),
                )?.label;
                if (ret) {
                    setValueLabel(ret);
                    return;
                } else if (typeof value === "string") {
                    setValueLabel(value);
                    return;
                }
                setValueLabel("");
            });
        }, [data, value]);

        const onDismiss = useCallback(() => {
            setIsVisible(false);
            onBlur?.();
        }, [onBlur]);

        return (
            <>
                <BaseField
                    value={valueLabel}
                    asListAction={asListAction}
                    label={label}
                    placeholder={placeholder}
                    right={right}
                    left={left}
                    style={style}
                    error={error}
                    disabled={props.disabled}
                    numberOfLines={numberOfLines}
                    isSelfContained={isSelfContained}
                    asButton
                    info={info}
                    componentRef={ref}
                    labelLeftPadding={labelLeftPadding}
                    component={
                        <PressableWithFocus
                            testID={testID}
                            disabled={isVisible || props.disabled}
                            onPress={() => {
                                if (KeyboardController.isVisible()) {
                                    void KeyboardController.dismiss();
                                }
                                setIsVisible(true);
                            }}
                        />
                    }
                />
                <ThemeProvider value={theme}>
                    <SelectChooser<V>
                        visible={isVisible}
                        onDismiss={onDismiss}
                        searchItems={data}
                        onSelect={onSelect}
                        placeholder={searchPlaceholder}
                        selectedValue={value}
                        bottomComponent={bottomComponent}
                        {...props}
                    />
                </ThemeProvider>
            </>
        );
    },
) as <V>(
    props: SelectFieldProps<V> & { ref?: React.ForwardedRef<TextInput> },
) => React.ReactElement;

export default SelectField;
